import { useEffect, useMemo, useRef, useState } from "react";
import { AreaSeries, CandlestickSeries, LineSeries, createChart, createSeriesMarkers } from "lightweight-charts";
import { palette } from "./pageStyles";
import { drawdownSeries } from "../backtest/metrics";

export const RUN_COLORS = ["#5fe0a8", "#a6c8ff", "#a497e6", "#ff7d8c", "#6fbfb3", "#c98aa6", "#9cb874", "#eceef1", "#8296ad"];
const WIN = "#5fe0a8";
const LOSS = "#ff7d8c";

const baseOptions = {
  autoSize: true,
  layout: { background: { color: "#050608" }, textColor: "#9097a3", attributionLogo: true },
  grid: { vertLines: { color: "rgba(26, 37, 56,0.8)" }, horzLines: { color: "rgba(26, 37, 56,0.8)" } },
  rightPriceScale: { borderColor: palette.border },
  timeScale: { borderColor: palette.border, timeVisible: true, secondsVisible: false },
  crosshair: { mode: 0 },
};

/** lightweight-charts needs strictly increasing unique times. */
function uniqueSorted(points) {
  const out = [];
  let last = -Infinity;
  [...points]
    .sort((a, b) => a.time - b.time)
    .forEach((p) => {
      const time = p.time <= last ? last + 1 : p.time;
      last = time;
      out.push({ ...p, time });
    });
  return out;
}

const fmtMoney = (v) => (Number.isFinite(v) ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "-");
const fmtPct = (v, d = 2) => (Number.isFinite(v) ? `${(v * 100).toFixed(d)}%` : "-");

/** Equity curves + drawdown + trade dots in ONE chart (three panes that zoom and scroll together).
 *  curves: [{name, color, times[], equity[], bold?, dashed?}] , focus: the curve whose drawdown and trades are drawn. */
export function EquityChart({ curves, focus, capital, height = 560 }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef([]);
  const [legend, setLegend] = useState(null);

  useEffect(() => {
    const chart = createChart(containerRef.current, { ...baseOptions });
    chartRef.current = chart;
    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = [];
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return undefined;
    seriesRef.current.forEach((s) => {
      try {
        chart.removeSeries(s);
      } catch {
        // already removed
      }
    });
    seriesRef.current = [];
    const track = [];
    const add = (type, opts, pane, data) => {
      const s = chart.addSeries(type, opts, pane);
      s.setData(data);
      seriesRef.current.push(s);
      return s;
    };

    // pane 0: equity curves
    curves.forEach((c) => {
      const s = add(
        LineSeries,
        {
          color: c.color,
          lineWidth: c.bold ? 3 : 2,
          lineStyle: c.dashed ? 2 : 0,
          priceLineVisible: false,
          lastValueVisible: true,
          crosshairMarkerVisible: true,
          priceFormat: { type: "price", precision: 0, minMove: 1 },
          title: c.name,
        },
        0,
        uniqueSorted(c.times.map((t, i) => ({ time: t, value: c.equity[i] })))
      );
      track.push({ s, name: c.name, color: c.color, kind: "equity" });
    });

    // pane 1: drawdown of the focus curve (area) and the others (thin lines)
    curves
      .filter((c) => !c.dashed && c !== focus)
      .forEach((c) => {
        add(
          LineSeries,
          {
            color: c.color,
            lineWidth: 1,
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false,
            priceFormat: { type: "custom", formatter: (v) => `${v.toFixed(1)}%`, minMove: 0.01 },
          },
          1,
          uniqueSorted(c.times.map((t, i) => ({ time: t, value: drawdownSeries(c.equity)[i] * 100 })))
        );
      });
    if (focus) {
      const dd = drawdownSeries(focus.equity);
      const s = add(
        AreaSeries,
        {
          lineColor: LOSS,
          topColor: "rgba(255, 125, 140,0.05)",
          bottomColor: "rgba(255, 125, 140,0.45)",
          invertFilledArea: true,
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: true,
          priceFormat: { type: "custom", formatter: (v) => `${v.toFixed(1)}%`, minMove: 0.01 },
          title: "Drawdown",
        },
        1,
        uniqueSorted(focus.times.map((t, i) => ({ time: t, value: dd[i] * 100 })))
      );
      track.push({ s, name: "Drawdown", color: LOSS, kind: "dd" });

      // pane 2: one dot per closed trade (return %), green wins / red losses
      const trades = focus.trades || [];
      const dots = (cond) =>
        uniqueSorted(trades.filter(cond).map((t) => ({ time: t.exitTime, value: t.ret * 100 })));
      const dotOpts = (color, title) => ({
        color,
        lineVisible: false,
        pointMarkersVisible: true,
        pointMarkersRadius: 3,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: true,
        priceFormat: { type: "custom", formatter: (v) => `${v.toFixed(2)}%`, minMove: 0.01 },
        title,
      });
      const wins = add(LineSeries, dotOpts(WIN, "Win %"), 2, dots((t) => t.pnl > 0));
      add(LineSeries, dotOpts(LOSS, "Loss %"), 2, dots((t) => t.pnl <= 0));
      wins.createPriceLine({ price: 0, color: "#46536a", lineWidth: 1, lineStyle: 2, axisLabelVisible: false, title: "" });
    }

    try {
      const panes = chart.panes();
      if (panes[0]?.setStretchFactor) panes[0].setStretchFactor(3);
      if (panes[1]?.setStretchFactor) panes[1].setStretchFactor(1.2);
      if (panes[2]?.setStretchFactor) panes[2].setStretchFactor(1.3);
    } catch {
      // default pane sizes are fine
    }
    chart.timeScale().fitContent();

    const onMove = (param) => {
      if (!param || !param.time || !param.seriesData) {
        setLegend(null);
        return;
      }
      const rows = track
        .map((t) => {
          const d = param.seriesData.get(t.s);
          return d && Number.isFinite(d.value) ? { name: t.name, color: t.color, value: d.value, kind: t.kind } : null;
        })
        .filter(Boolean);
      setLegend({ time: param.time, rows });
    };
    chart.subscribeCrosshairMove(onMove);
    return () => chart.unsubscribeCrosshairMove(onMove);
  }, [curves, focus, capital]);

  return (
    <div style={{ position: "relative", border: `1px solid ${palette.border}`, borderRadius: 8, overflow: "hidden" }}>
      <div ref={containerRef} style={{ height, width: "100%" }} />
      <div style={legendBox}>
        {legend ? (
          <>
            <div style={{ color: palette.mutedText }}>{new Date(legend.time * 1000).toISOString().replace("T", " ").slice(0, 16)} UTC</div>
            {legend.rows.map((r) => (
              <div key={r.name} style={{ color: r.color }}>
                {r.name}: {r.kind === "dd" ? `${r.value.toFixed(2)}%` : fmtMoney(r.value)}
              </div>
            ))}
          </>
        ) : (
          <div style={{ color: palette.mutedText }}>Top: equity · middle: drawdown · bottom: one dot per trade. Scroll to zoom, drag to pan.</div>
        )}
      </div>
    </div>
  );
}

const legendBox = {
  position: "absolute",
  top: 8,
  left: 10,
  fontSize: "0.72rem",
  lineHeight: 1.45,
  pointerEvents: "none",
  background: "rgba(16,19,27,0.72)",
  padding: "4px 6px",
  borderRadius: 4,
  zIndex: 3,
  maxWidth: "70%",
};

function pricePrecision(candles) {
  const last = candles && candles.length ? Math.abs(candles[candles.length - 1].close) : 1;
  if (last >= 1000) return 2;
  if (last >= 100) return 3;
  if (last >= 10) return 4;
  return 5;
}

/** Candles with every trade of the focus run: arrow at the entry, dot with the result at the exit. */
export function TradesPriceChart({ candles, trades, zoomTo, height = 380 }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleRef = useRef(null);
  const markersRef = useRef(null);

  useEffect(() => {
    const chart = createChart(containerRef.current, { ...baseOptions });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: WIN,
      downColor: LOSS,
      wickUpColor: WIN,
      wickDownColor: LOSS,
      borderVisible: false,
    });
    chartRef.current = chart;
    candleRef.current = series;
    markersRef.current = createSeriesMarkers(series, []);
    return () => {
      chart.remove();
      chartRef.current = null;
      candleRef.current = null;
      markersRef.current = null;
    };
  }, []);

  useEffect(() => {
    const series = candleRef.current;
    if (!series) return;
    const rows = uniqueSorted((candles || []).map((c) => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close })));
    const p = pricePrecision(candles);
    series.applyOptions({ priceFormat: { type: "price", precision: p, minMove: Math.pow(10, -p) } });
    series.setData(rows);
    chartRef.current?.timeScale().fitContent();
  }, [candles]);

  useEffect(() => {
    const list = (trades || []).slice(-1500);
    const showText = list.length <= 40; // keep dense charts readable; the selected trade is still labelled
    const markers = [];
    list.forEach((t) => {
      const focused = zoomTo && t.entryTime === zoomTo.entryTime && t.exitTime === zoomTo.exitTime;
      const labelTrade = showText || focused;
      const resultText = `${t.ret >= 0 ? "+" : ""}${(t.ret * 100).toFixed(1)}%`;
      const exitLabel = t.reason === "target"
        ? "TP"
        : t.reason === "stop"
          ? "SL"
          : t.reason === "signal"
            ? (t.side === 1 ? "SELL" : "COVER")
            : "CLOSE";
      markers.push(
        t.side === 1
          ? { time: t.entryTime, position: "belowBar", color: WIN, shape: "arrowUp", text: labelTrade ? "BUY" : "" }
          : { time: t.entryTime, position: "aboveBar", color: LOSS, shape: "arrowDown", text: labelTrade ? "SHORT" : "" }
      );
      markers.push({
        time: t.exitTime,
        position: t.side === 1 ? "aboveBar" : "belowBar",
        color: t.pnl > 0 ? WIN : LOSS,
        shape: "circle",
        text: labelTrade ? `${exitLabel} ${resultText}` : "",
      });
    });
    markers.sort((a, b) => a.time - b.time);
    markersRef.current?.setMarkers(markers);
  }, [trades, candles, zoomTo]);

  useEffect(() => {
    if (!zoomTo || !chartRef.current) return;
    const pad = Math.max((zoomTo.exitTime - zoomTo.entryTime) * 1.5, 3600 * 6);
    try {
      chartRef.current.timeScale().setVisibleRange({ from: zoomTo.entryTime - pad, to: zoomTo.exitTime + pad });
    } catch {
      // range outside the data: ignore
    }
  }, [zoomTo]);

  return (
    <div style={{ position: "relative", border: `1px solid ${palette.border}`, borderRadius: 8, overflow: "hidden" }}>
      <div ref={containerRef} style={{ height, width: "100%" }} />
    </div>
  );
}

/** Distribution of trade returns, drawn as plain SVG. */
export function ReturnHistogram({ bins, unit = "%" }) {
  if (!bins || !bins.length) return <div style={{ color: palette.mutedText, fontSize: "0.85rem" }}>No trades yet.</div>;
  const W = 520;
  const H = 180;
  const maxC = Math.max(...bins.map((b) => b.count), 1);
  const bw = W / bins.length;
  return (
    <svg viewBox={`0 0 ${W} ${H + 24}`} style={{ width: "100%", height: "auto" }} role="img" aria-label="Trade return distribution">
      {bins.map((b, i) => {
        const h = (b.count / maxC) * H;
        const mid = (b.from + b.to) / 2;
        return (
          <g key={i}>
            <rect x={i * bw + 1} y={H - h} width={Math.max(bw - 2, 1)} height={h} fill={mid >= 0 ? WIN : LOSS} opacity={0.85}>
              <title>{unit === "R" ? `${b.from.toFixed(2)}R to ${b.to.toFixed(2)}R: ${b.count} trades` : `${(b.from * 100).toFixed(2)}% to ${(b.to * 100).toFixed(2)}%: ${b.count} trades`}</title>
            </rect>
          </g>
        );
      })}
      <line x1={0} x2={W} y1={H} y2={H} stroke={palette.border} />
      <text x={2} y={H + 16} fontSize="11" fill={palette.mutedText}>{unit === "R" ? `${bins[0].from.toFixed(1)}R` : fmtPct(bins[0].from, 1)}</text>
      <text x={W - 2} y={H + 16} fontSize="11" fill={palette.mutedText} textAnchor="end">{unit === "R" ? `${bins[bins.length - 1].to.toFixed(1)}R` : fmtPct(bins[bins.length - 1].to, 1)}</text>
    </svg>
  );
}

/** Year x month table coloured by return. */
export function MonthlyTable({ months }) {
  const byYear = useMemo(() => {
    const map = new Map();
    (months || []).forEach(({ key, ret }) => {
      const [y, m] = key.split("-");
      if (!map.has(y)) map.set(y, new Array(12).fill(null));
      map.get(y)[Number(m) - 1] = ret;
    });
    return [...map.entries()];
  }, [months]);
  if (!byYear.length) return null;
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const cell = (v) => {
    if (v === null) return { text: "", style: {} };
    const a = Math.min(Math.abs(v) / 0.05, 1) * 0.55 + 0.08;
    return { text: `${(v * 100).toFixed(1)}`, style: { background: v >= 0 ? `rgba(95, 224, 168,${a})` : `rgba(255, 125, 140,${a})` } };
  };
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: "0.75rem", width: "100%", minWidth: 560 }}>
        <thead>
          <tr>
            <th style={th}></th>
            {names.map((n) => (
              <th key={n} style={th}>{n}</th>
            ))}
            <th style={th}>Year</th>
          </tr>
        </thead>
        <tbody>
          {byYear.map(([year, vals]) => {
            const total = vals.reduce((a, v) => (v === null ? a : a * (1 + v)), 1) - 1;
            const t = cell(total);
            return (
              <tr key={year}>
                <td style={{ ...td, color: palette.mutedText }}>{year}</td>
                {vals.map((v, i) => {
                  const c = cell(v);
                  return (
                    <td key={i} style={{ ...td, ...c.style, textAlign: "center" }}>{c.text}</td>
                  );
                })}
                <td style={{ ...td, ...t.style, textAlign: "center", fontWeight: 600 }}>{t.text}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ color: palette.mutedText, fontSize: "0.7rem", marginTop: 4 }}>Monthly return in %</div>
    </div>
  );
}

const th = { padding: "4px 6px", color: palette.mutedText, fontWeight: 500, textAlign: "center", borderBottom: `1px solid ${palette.border}` };
const td = { padding: "4px 6px", borderBottom: `1px solid ${palette.border}` };
